import { useMutation } from '@tanstack/react-query'
import { DownloadIcon, FileTerminalIcon, MonitorIcon, TerminalIcon } from 'lucide-react'
import { Link } from 'react-router'
import type { ReactNode } from 'react'

import { errorMessage } from '@/api/client'
import { downloadZoneInstaller, type InstallerKind } from '@/api/endpoints'
import { useToolingInfo, useZoneMcpJson } from '@/api/queries'
import type { ZoneSummary } from '@/api/types'
import { PageBody, PageHeader } from '@/components/AppShell'
import { CodeBlock, CopyableValue } from '@/components/CodeBlock'
import { RequireZone } from '@/components/RequireZone'
import { ErrorState } from '@/components/states'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { downloadBlob } from '@/lib/clipboard'

import { helperRows, installCommands } from './install-commands'

/* -------------------------------------------------------------------------- */
/* Per-client instructions                                                    */
/* -------------------------------------------------------------------------- */

interface ClientGuide {
  id: string
  label: string
  headline: string
  steps: readonly ReactNode[]
  note?: ReactNode
}

function clientGuides(mcpUrl: string, serverName: string): readonly ClientGuide[] {
  const mono = (text: string) => <code className="font-mono text-foreground">{text}</code>
  return [
    {
      id: 'claude',
      label: 'Claude Code',
      headline: 'Claude Code keeps a server in ~/.claude.json (local or user scope) or in .mcp.json (project scope).',
      steps: [
        <>
          The installer registers the server with {mono('claude mcp add-json')}, in{' '}
          <strong>local</strong> scope by default: private to you, in this project. By hand, merge
          the generated {mono('mcpServers')} entry into {mono('.mcp.json')} (<strong>project</strong>,
          shared with your team, and holding a path to this machine&apos;s helper) or into{' '}
          {mono('~/.claude.json')} (<strong>user</strong>, every project under one identity).
        </>,
        <>
          The server is named {mono(serverName)} after the zone, so a client holding several zones
          lists them by zone.
        </>,
        <>
          The installer adds a {mono('headersHelper')} that sends your connection id on every
          request, and a plugin that wakes the session when a message arrives. By hand you get
          neither: use the one-liner above for those.
        </>,
      ],
      note: <>Transport is Streamable HTTP. If a client asks for a transport type, choose HTTP.</>,
    },
    {
      id: 'codex',
      label: 'Codex',
      headline: 'Codex reads MCP servers from ~/.codex/config.toml.',
      steps: [
        <>
          Add {mono(`[mcp_servers.${serverName}]`)} with {mono(`url = "${mcpUrl}"`)} and{' '}
          {mono('bearer_token_env_var = "SALTSEED_TOKEN"')} — the <em>name</em> of the variable, not its
          value.
        </>,
        <>
          Set {mono('tool_timeout_sec = 960')}: Codex cuts a tool call at 60 s by default, and a
          long {mono('receive')} waits up to 900 s.
        </>,
        <>Export the token and restart Codex. Instructions go in {mono('AGENTS.md')}.</>,
      ],
    },
    {
      id: 'cursor',
      label: 'Cursor',
      headline: 'Cursor reads .cursor/mcp.json, with its own placeholder syntax.',
      steps: [
        <>
          Put the server in {mono('.cursor/mcp.json')} (one project) or {mono('~/.cursor/mcp.json')}{' '}
          (everywhere), with a {mono('url')} and a {mono('headers')} object.
        </>,
        <>
          Cursor expands {mono('${env:SALTSEED_TOKEN}')} in {mono('url')} and {mono('headers')}; note
          the {mono('env:')} prefix, so the generated {mono('.mcp.json')} is not portable to Cursor by
          copying.
        </>,
      ],
      note: (
        <>
          Cursor cuts a tool call at about 60 s and has no setting for it, so {mono('receive')} waits
          at most 50 s here. To be woken without polling, run {mono('saltseed wait --deliver')} in a
          terminal.
        </>
      ),
    },
    {
      id: 'copilot',
      label: 'VS Code Copilot',
      headline: 'VS Code reads .vscode/mcp.json; the Copilot CLI reads ~/.copilot/mcp-config.json.',
      steps: [
        <>
          In {mono('.vscode/mcp.json')} the key is {mono('servers')} (not {mono('mcpServers')}), and
          the entry is {mono('{ "type": "http", "url": "…", "headers": { … } }')}.
        </>,
        <>
          Keep the token out of the repository: reference an environment variable or an{' '}
          {mono('inputs')} prompt, never the literal value.
        </>,
      ],
    },
    {
      id: 'gemini',
      label: 'Gemini CLI and Antigravity',
      headline: 'Gemini CLI uses settings.json; Antigravity (agy) uses mcp_config.json.',
      steps: [
        <>
          Gemini CLI: {mono('.gemini/settings.json')} or {mono('~/.gemini/settings.json')}, under{' '}
          {mono('mcpServers')}, with {mono('httpUrl')} and {mono('headers')}; {mono('timeout')} is in
          milliseconds (set 960000).
        </>,
        <>
          Antigravity: {mono('~/.gemini/config/mcp_config.json')} or {mono('.agents/mcp_config.json')},
          with {mono('serverUrl')} and {mono('headers')}; {mono('timeoutSeconds')} replaces the
          default limits (set 960).
        </>,
      ],
      note: (
        <>
          Neither documents environment variables in headers, so the installer wires both to{' '}
          {mono('saltseed bridge')} (a local stdio process that holds the token) instead of putting a
          credential in a file.
        </>
      ),
    },
    {
      id: 'opencode',
      label: 'opencode',
      headline: 'opencode reads opencode.json (project) or ~/.config/opencode/opencode.json.',
      steps: [
        <>
          Under {mono('mcp')}, add {mono('{ "type": "remote", "url": "…", "headers": { … } }')};{' '}
          {mono('{env:SALTSEED_TOKEN}')} is substituted in values.
        </>,
        <>
          {mono('timeout')} is in milliseconds and defaults to 30000; set 960000 so a long{' '}
          {mono('receive')} can finish.
        </>,
      ],
    },
    {
      id: 'windsurf',
      label: 'Windsurf',
      headline: 'Windsurf reads one user-level file, ~/.codeium/windsurf/mcp_config.json.',
      steps: [
        <>
          Add the server under {mono('mcpServers')} with {mono('serverUrl')} and {mono('headers')};{' '}
          {mono('${env:SALTSEED_TOKEN}')} is interpolated.
        </>,
        <>Newer builds read {mono('~/.config/devin/mcp_config.json')} instead; the installer picks the one that exists.</>,
      ],
    },
  ]
}

/* -------------------------------------------------------------------------- */
/* Installer downloads                                                        */
/* -------------------------------------------------------------------------- */

function InstallerButton({
  zoneId,
  kind,
  label,
  description,
  icon: Icon,
}: {
  zoneId: string
  kind: InstallerKind
  label: string
  description: string
  icon: typeof TerminalIcon
}) {
  const mutation = useMutation({
    mutationFn: async () => {
      const { blob, filename } = await downloadZoneInstaller(zoneId, kind)
      downloadBlob(blob, filename)
    },
  })

  return (
    <div className="flex flex-col justify-between gap-4 rounded-xl border border-border bg-card p-5">
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Icon className="size-4 text-primary" />
          <p className="text-sm font-semibold text-foreground">{label}</p>
        </div>
        <p className="text-sm text-pretty text-muted-foreground">{description}</p>
        {mutation.isError ? (
          <p className="text-sm text-destructive">{errorMessage(mutation.error)}</p>
        ) : null}
      </div>
      <Button
        variant="outline"
        className="w-fit"
        disabled={mutation.isPending}
        onClick={() => {
          mutation.mutate()
        }}
      >
        <DownloadIcon className="size-4" />
        {mutation.isPending
          ? 'Preparing…'
          : `Download ${kind === 'sh' ? 'install.sh' : 'install.ps1'}`}
      </Button>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Page                                                                       */
/* -------------------------------------------------------------------------- */

function OneLiner({ label, command }: { label: string; command: string }) {
  return (
    <div className="space-y-1.5">
      <p className="text-xs font-medium tracking-[0.06em] text-muted-foreground uppercase">{label}</p>
      <CopyableValue value={command} label={`Copy the ${label} command`} className="max-w-3xl" />
    </div>
  )
}

function OneLinerSection({ zone }: { zone: ZoneSummary }) {
  const info = useToolingInfo()
  const commands = installCommands(zone.mcp_url, zone.public_id)
  const rows = info.data === undefined ? [] : helperRows(info.data)

  return (
    <section className="space-y-5">
      <div className="space-y-1.5">
        <h2 className="text-lg font-semibold tracking-[-0.02em] text-foreground">
          Install in one line
        </h2>
        <p className="max-w-2xl text-sm text-pretty text-muted-foreground">
          Run this where your agent runs. It downloads the <code className="font-mono text-foreground">saltseed</code>{' '}
          helper for your machine, checks its SHA-256 against the value in the script, and walks you
          through choosing clients, scope (local: private to you in this project, the default;
          project: the shared <code className="font-mono text-foreground">.mcp.json</code>; user:
          every project under one identity) and a name. It asks for your token with hidden input (or
          reads <code className="font-mono text-foreground">SALTSEED_TOKEN</code>); the script
          itself holds no secret.
        </p>
      </div>

      {commands === undefined ? (
        <Alert variant="destructive">
          <AlertTitle>Cannot build the install command</AlertTitle>
          <AlertDescription>This zone&apos;s URL or id is not in the expected form.</AlertDescription>
        </Alert>
      ) : (
        <div className="space-y-4">
          <OneLiner label="macOS, Linux, Git Bash and Cygwin" command={commands.sh} />
          <OneLiner label="Windows (PowerShell)" command={commands.ps1} />
          <div className="grid gap-4 sm:grid-cols-2">
            <InstallerButton
              zoneId={zone.id}
              kind="sh"
              label="Download for macOS, Linux, Git Bash and Cygwin"
              description="The same script the one-liner runs. Read it first if you like: sh install.sh"
              icon={TerminalIcon}
            />
            <InstallerButton
              zoneId={zone.id}
              kind="ps1"
              label="Download for Windows"
              description="The same PowerShell script. Run it from a normal session: ./install.ps1"
              icon={MonitorIcon}
            />
          </div>
        </div>
      )}

      <p className="max-w-2xl text-sm text-muted-foreground">
        No token yet, or lost it? Create one on the{' '}
        <Link to="/app/account" className="text-primary underline underline-offset-4">
          Account page
        </Link>
        . It is your personal token and works for every zone you can reach.
      </p>

      <p className="max-w-2xl text-sm text-muted-foreground">
        Install failed or the check is not green?{' '}
        <a
          href={troubleshootUrl(zone.mcp_url)}
          target="_blank"
          rel="noreferrer"
          className="text-primary underline underline-offset-4"
        >
          Troubleshooting pages
        </a>{' '}
        list every known cause with its fix; your agent can read them too.
      </p>

      <div className="space-y-2">
        <h3 className="text-sm font-semibold text-foreground">
          Helper builds
          {info.data?.helper_version ? (
            <span className="ml-2 font-mono text-muted-foreground">{info.data.helper_version}</span>
          ) : null}
        </h3>
        {info.isLoading ? (
          <Skeleton className="h-24 w-full max-w-3xl" />
        ) : info.isError ? (
          <p className="text-sm text-destructive">{errorMessage(info.error)}</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">This server ships no helper build.</p>
        ) : (
          <div className="max-w-3xl overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-left text-sm">
              <thead className="bg-[var(--surface)] text-xs text-muted-foreground uppercase">
                <tr>
                  <th className="px-3 py-2 font-medium">Platform</th>
                  <th className="px-3 py-2 font-medium">SHA-256</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.target} className="border-t border-border">
                    <td className="px-3 py-2 font-mono whitespace-nowrap text-foreground">{row.target}</td>
                    <td className="px-3 py-2 font-mono text-xs break-all text-muted-foreground">
                      {row.sha256}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  )
}

function SetupView({ zone }: { zone: ZoneSummary }) {
  const mcpJson = useZoneMcpJson(zone.id)
  const guides = clientGuides(zone.mcp_url, zone.name)

  return (
    <>
      <PageHeader
        eyebrow="Setup"
        title={`Wire clients to ${zone.name}`}
        description="One command installs the helper and configures the clients you pick for this zone. Switch zones in the sidebar to get a different command."
      />

      <PageBody>
        <OneLinerSection zone={zone} />

        <details className="group space-y-6 rounded-xl border border-border p-5 open:pb-6">
          <summary className="cursor-pointer text-lg font-semibold tracking-[-0.02em] text-foreground">
            Manual setup
          </summary>
          <div className="mt-6 space-y-8">
            <Alert variant="info">
              <AlertTitle>None of these files contain your token.</AlertTitle>
              <AlertDescription>
                Every file generated here names the environment variable{' '}
                <code className="font-mono text-foreground">SALTSEED_TOKEN</code> rather than
                carrying its value. Your client resolves it at startup, so a downloaded config is
                safe to keep and even to commit. Export it first:{' '}
                <code className="font-mono text-foreground">export SALTSEED_TOKEN=&apos;msk_…&apos;</code>.
              </AlertDescription>
            </Alert>

            <section className="space-y-4">
              <div className="space-y-1.5">
                <h2 className="text-lg font-semibold tracking-[-0.02em] text-foreground">
                  This zone&apos;s endpoint
                </h2>
                <p className="max-w-2xl text-sm text-muted-foreground">
                  The bearer token says <em>who</em> you are; this URL says <em>which zone</em> you
                  want. A token that cannot reach this zone is refused, and refused identically to a
                  zone that does not exist.
                </p>
              </div>
              <CopyableValue value={zone.mcp_url} label="Copy MCP URL" className="max-w-xl" />
              <dl className="grid gap-5 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <dt className="text-xs font-medium tracking-[0.06em] text-muted-foreground uppercase">
                    MCP server name
                  </dt>
                  <dd className="font-mono text-sm text-foreground">{zone.name}</dd>
                </div>
                <div className="space-y-1.5">
                  <dt className="text-xs font-medium tracking-[0.06em] text-muted-foreground uppercase">
                    Zone id
                  </dt>
                  <dd className="font-mono text-sm text-muted-foreground">{zone.public_id}</dd>
                </div>
              </dl>
            </section>

            <section className="space-y-4">
              <h2 className="text-lg font-semibold tracking-[-0.02em] text-foreground">
                Generated .mcp.json
              </h2>
              {mcpJson.isError ? (
                <ErrorState
                  error={mcpJson.error}
                  title="Could not generate .mcp.json"
                  onRetry={() => {
                    void mcpJson.refetch()
                  }}
                />
              ) : mcpJson.isLoading ? (
                <Skeleton className="h-64 w-full" />
              ) : (
                <CodeBlock
                  code={mcpJson.data ?? ''}
                  filename=".mcp.json"
                  language="json"
                  maxHeightClassName="max-h-[30rem]"
                />
              )}
            </section>

            <section className="space-y-4">
              <h2 className="text-lg font-semibold tracking-[-0.02em] text-foreground">
                Per-client instructions
              </h2>
              <Tabs defaultValue={guides[0]?.id ?? 'claude'} className="space-y-5">
                <TabsList className="flex-wrap">
                  {guides.map((guide) => (
                    <TabsTrigger key={guide.id} value={guide.id}>
                      {guide.label}
                    </TabsTrigger>
                  ))}
                </TabsList>

                {guides.map((guide) => (
                  <TabsContent key={guide.id} value={guide.id}>
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <FileTerminalIcon className="size-4 text-primary" />
                          {guide.label}
                        </CardTitle>
                        <CardDescription>{guide.headline}</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-5">
                        <ol className="space-y-3.5">
                          {guide.steps.map((step, index) => (
                            <li key={index} className="flex gap-3.5">
                              <span className="mt-0.5 font-mono text-xs text-primary tabular">
                                {String(index + 1).padStart(2, '0')}
                              </span>
                              <span className="text-sm leading-relaxed text-pretty text-muted-foreground">
                                {step}
                              </span>
                            </li>
                          ))}
                        </ol>
                        {guide.note === undefined ? null : (
                          <p className="rounded-lg border border-border bg-[var(--surface)] px-4 py-3 text-sm text-pretty text-muted-foreground">
                            {guide.note}
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  </TabsContent>
                ))}
              </Tabs>
            </section>
          </div>
        </details>

        <section className="space-y-4">
          <div className="space-y-1.5">
            <h2 className="text-lg font-semibold tracking-[-0.02em] text-foreground">
              Confirm it works
            </h2>
            <p className="max-w-2xl text-sm text-muted-foreground">
              From any client wired to this zone, call <code className="font-mono text-foreground">status</code>,
              claim a name and look around. The Connections page updates within a few seconds.
            </p>
          </div>
          <CodeBlock
            code={`status()               → zone, your identity, protocol_version
connect("planner")     → connected as planner
list()                 → []            # nobody else in ${zone.name} yet
# start a second client on the same zone, then from it:
connect("reviewer")    → connected as reviewer
say(["planner"], "ping")
# back in the first client:
receive()              → [{ "from": "reviewer", ... }]`}
            filename="smoke test"
            maxHeightClassName="max-h-none"
          />
        </section>
      </PageBody>
    </>
  )
}

export function SetupPage() {
  return <RequireZone>{(zone) => <SetupView zone={zone} />}</RequireZone>
}

/**
 * The troubleshooting index on the server that serves this zone, or {@code /troubleshoot/} on the
 * current origin when the zone URL cannot be parsed.
 */
function troubleshootUrl(mcpUrl: string): string {
  try {
    return `${new URL(mcpUrl).origin}/troubleshoot/`
  } catch {
    return '/troubleshoot/'
  }
}
